/**
 * Motor de layout del mapa mental (estilo XMind), puro y determinista.
 *
 * Recibe los temas como datos planos y devuelve posición, tamaño y campos
 * derivados de cada uno. Quien MUTA el árbol corre el layout y escribe el
 * resultado en el documento dentro de la misma transacción: los demás clientes
 * solo leen figuras normales, así que la selección, el borrador, la exportación
 * y la sincronización funcionan sin saber nada de mapas.
 *
 * Tres estructuras:
 *  - 'map'  : balanceado — las ramas del tema central se reparten a ambos lados
 *  - 'right': lógico — todo crece hacia la derecha
 *  - 'down' : organigrama — todo crece hacia abajo
 */
import type { MapLayoutId, ShapeId } from '../../shared/types.ts'

export interface TopicInput {
  id: ShapeId
  parentId: ShapeId | null
  order: number
  collapsed?: boolean
  text: string
  /** anclas de raíces y temas flotantes (los hijos las ignoran) */
  x: number
  y: number
}

export interface TopicPlacement {
  x: number
  y: number
  w: number
  h: number
  depth: number
  branchColor: string
  hidden: boolean
  hiddenCount: number
  /** de qué lado cuelga del padre (para el conector y la insignia de plegado) */
  side: 'right' | 'left' | 'down'
}

export interface MeasureFn {
  /** `id` permite reutilizar la medida ya acordada de ese tema (ver relayout). */
  (text: string, depth: number, id?: ShapeId): { w: number; h: number }
}

/** Colores de rama, en el orden en que se asignan a los hijos del tema central. */
export const BRANCH_COLORS = [
  '#F0561D',
  '#0F6CBD',
  '#0E700E',
  '#B4009E',
  '#D83B01',
  '#5C2E91',
  '#038387',
  '#986F0B',
] as const

/** Color del tema central y de los temas flotantes. */
export const ROOT_COLOR = '#26262B'

const GAP_LEVEL_X = 46
const GAP_SIBLING_Y = 14
const GAP_LEVEL_Y = 54
const GAP_SIBLING_X = 20

interface Node {
  topic: TopicInput
  children: Node[]
  depth: number
  branchColor: string
  w: number
  h: number
  /** alto (u ancho, según eje) total del subárbol visible */
  extent: number
  hiddenCount: number
}

interface Forest {
  roots: Node[]
  /** ids que quedaron atrapados en un ciclo y se rescataron como raíces */
  rescued: ShapeId[]
}

/** Los árboles del tablero: cada raíz (tema central o flotante) con sus hijos ordenados. */
function buildForest(topics: TopicInput[]): Forest {
  const byParent = new Map<ShapeId | null, TopicInput[]>()
  const ids = new Set(topics.map((t) => t.id))
  for (const t of topics) {
    // Un padre desaparecido (borrado en paralelo) convierte al hijo en flotante.
    const parent = t.parentId !== null && ids.has(t.parentId) ? t.parentId : null
    const list = byParent.get(parent) ?? []
    list.push(t)
    byParent.set(parent, list)
  }
  for (const list of byParent.values()) {
    list.sort((a, b) => a.order - b.order || a.id.localeCompare(b.id))
  }
  const visited = new Set<ShapeId>()
  const make = (topic: TopicInput, depth: number, branchColor: string): Node => {
    visited.add(topic.id)
    const children = (byParent.get(topic.id) ?? [])
      // Un ciclo real (dos clientes recolgando a la vez) no puede colgarnos.
      .filter((child) => !visited.has(child.id))
      .map((child, i) =>
        make(child, depth + 1, depth === 0 ? BRANCH_COLORS[i % BRANCH_COLORS.length] : branchColor),
      )
    return { topic, children, depth, branchColor, w: 0, h: 0, extent: 0, hiddenCount: 0 }
  }
  const roots = (byParent.get(null) ?? []).map((root) => make(root, 0, ROOT_COLOR))
  // Temas atrapados en un ciclo sin raíz (A→B→A): el de id menor se vuelve raíz
  // deterministamente en TODOS los clientes, y el resto cuelga de él.
  const rescued: ShapeId[] = []
  const perdidos = topics.filter((t) => !visited.has(t.id)).sort((a, b) => a.id.localeCompare(b.id))
  for (const t of perdidos) {
    if (visited.has(t.id)) continue
    rescued.push(t.id)
    roots.push(make(t, 0, ROOT_COLOR))
  }
  return { roots, rescued }
}

function countDescendants(node: Node): number {
  return node.children.reduce((sum, child) => sum + 1 + countDescendants(child), 0)
}

/** Mide cada nodo y calcula la extensión del subárbol en el eje de apilado. */
function measureTree(node: Node, measure: MeasureFn, axis: 'y' | 'x'): void {
  const size = measure(node.topic.text, node.depth, node.topic.id)
  node.w = size.w
  node.h = size.h
  node.hiddenCount = node.topic.collapsed ? countDescendants(node) : 0
  const own = axis === 'y' ? node.h : node.w
  if (node.topic.collapsed || node.children.length === 0) {
    node.extent = own
    return
  }
  const gap = axis === 'y' ? GAP_SIBLING_Y : GAP_SIBLING_X
  let total = 0
  for (const child of node.children) {
    measureTree(child, measure, axis)
    total += child.extent
  }
  total += gap * (node.children.length - 1)
  node.extent = Math.max(own, total)
}

/** Coloca un subárbol horizontal: el nodo en x, centrado en cy, hijos hacia dir. */
function placeHorizontal(
  node: Node,
  x: number,
  cy: number,
  dir: 1 | -1,
  out: Map<ShapeId, TopicPlacement>,
  hidden: boolean,
): void {
  out.set(node.topic.id, {
    x: dir === 1 ? x : x - node.w,
    y: cy - node.h / 2,
    w: node.w,
    h: node.h,
    depth: node.depth,
    branchColor: node.branchColor,
    hidden,
    hiddenCount: node.hiddenCount,
    side: dir === 1 ? 'right' : 'left',
  })
  if (node.children.length === 0) return
  const childrenHidden = hidden || !!node.topic.collapsed
  const total =
    node.children.reduce((s, c) => s + c.extent, 0) + GAP_SIBLING_Y * (node.children.length - 1)
  let cursor = cy - total / 2
  const childX = dir === 1 ? x + node.w + GAP_LEVEL_X : x - node.w - GAP_LEVEL_X
  for (const child of node.children) {
    placeHorizontal(child, childX, cursor + child.extent / 2, dir, out, childrenHidden)
    cursor += child.extent + GAP_SIBLING_Y
  }
}

/** Coloca un subárbol vertical (organigrama): el nodo centrado en cx, hijos debajo. */
function placeVertical(
  node: Node,
  cx: number,
  y: number,
  out: Map<ShapeId, TopicPlacement>,
  hidden: boolean,
): void {
  out.set(node.topic.id, {
    x: cx - node.w / 2,
    y,
    w: node.w,
    h: node.h,
    depth: node.depth,
    branchColor: node.branchColor,
    hidden,
    hiddenCount: node.hiddenCount,
    side: 'down',
  })
  if (node.children.length === 0) return
  const childrenHidden = hidden || !!node.topic.collapsed
  const total =
    node.children.reduce((s, c) => s + c.extent, 0) + GAP_SIBLING_X * (node.children.length - 1)
  let cursor = cx - total / 2
  const childY = y + node.h + GAP_LEVEL_Y
  for (const child of node.children) {
    placeVertical(child, cursor + child.extent / 2, childY, out, childrenHidden)
    cursor += child.extent + GAP_SIBLING_X
  }
}

/**
 * Layout completo del tablero. Cada raíz ancla su árbol en su propia (x, y)
 * —el CENTRO del tema raíz— así que mover el tema central mueve todo el mapa.
 */
export interface LayoutResult {
  placements: Map<ShapeId, TopicPlacement>
  /** temas que un ciclo dejó sin árbol y que deben volverse flotantes de verdad */
  rescatados: ShapeId[]
}

export function layoutMindMap(topics: TopicInput[], layout: MapLayoutId, measure: MeasureFn): LayoutResult {
  const out = new Map<ShapeId, TopicPlacement>()
  const forest = buildForest(topics)

  for (const root of forest.roots) {
    const anchorX = root.topic.x
    const anchorY = root.topic.y

    if (layout === 'down') {
      measureTree(root, measure, 'x')
      // el ancla (x, y) de la raíz es su CENTRO
      placeVertical(root, anchorX, anchorY - measure(root.topic.text, 0, root.topic.id).h / 2, out, false)
      continue
    }

    if (layout === 'right') {
      measureTree(root, measure, 'y')
      placeHorizontal(root, anchorX - root.w / 2, anchorY, 1, out, false)
      continue
    }

    // 'map': balanceado. Las ramas del tema central se alternan derecha/izquierda;
    // los flotantes sin hijos o con estructura corta crecen a la derecha.
    const right: Node[] = []
    const left: Node[] = []
    root.children.forEach((child, i) => (i % 2 === 0 ? right : left).push(child))

    const rootSize = measure(root.topic.text, 0, root.topic.id)
    root.w = rootSize.w
    root.h = rootSize.h
    root.hiddenCount = root.topic.collapsed ? countDescendants(root) : 0

    out.set(root.topic.id, {
      x: anchorX - root.w / 2,
      y: anchorY - root.h / 2,
      w: root.w,
      h: root.h,
      depth: 0,
      branchColor: ROOT_COLOR,
      hidden: false,
      hiddenCount: root.hiddenCount,
      side: 'right',
    })
    const childrenHidden = !!root.topic.collapsed

    for (const [lado, dir] of [
      [right, 1],
      [left, -1],
    ] as const) {
      if (lado.length === 0) continue
      for (const child of lado) measureTree(child, measure, 'y')
      const total = lado.reduce((s, c) => s + c.extent, 0) + GAP_SIBLING_Y * (lado.length - 1)
      let cursor = anchorY - total / 2
      const childX = dir === 1 ? anchorX + root.w / 2 + GAP_LEVEL_X : anchorX - root.w / 2 - GAP_LEVEL_X
      for (const child of lado) {
        placeHorizontal(child, childX, cursor + child.extent / 2, dir, out, childrenHidden)
        cursor += child.extent + GAP_SIBLING_Y
      }
    }
  }
  return { placements: out, rescatados: forest.rescued }
}

/* ── Utilidades de árbol para las acciones ───────────────────────────── */

/** Ids del subárbol de `id`, incluido él mismo. */
export function subtreeIds(topics: { id: ShapeId; parentId: ShapeId | null }[], id: ShapeId): Set<ShapeId> {
  const byParent = new Map<ShapeId | null, ShapeId[]>()
  for (const t of topics) {
    const list = byParent.get(t.parentId) ?? []
    list.push(t.id)
    byParent.set(t.parentId, list)
  }
  const out = new Set<ShapeId>()
  const walk = (current: ShapeId): void => {
    if (out.has(current)) return
    out.add(current)
    for (const child of byParent.get(current) ?? []) walk(child)
  }
  walk(id)
  return out
}

/** ¿Se puede colgar `id` de `target`? (nunca de sí mismo ni de un descendiente) */
export function canReparent(
  topics: { id: ShapeId; parentId: ShapeId | null }[],
  id: ShapeId,
  target: ShapeId,
): boolean {
  if (id === target) return false
  return !subtreeIds(topics, id).has(target)
}
